import React, { useMemo, useState } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { latestFinancialObservations } from "./financeObservations";
import { metricDisplayValue, usePlatformData } from "../../hooks/usePlatformData";

const PERIODS = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["all", "All time"]];

function periodStart(period) {
  if (period === "all") return null;
  const date = new Date();
  date.setDate(date.getDate() - Number.parseInt(period, 10));
  return date;
}

// One group per currency; observations in different currencies are never combined.
function groupByCurrency(rows) {
  const groups = new Map();
  for (const row of rows) {
    const currency = String(row.currency || "Unknown currency").toUpperCase();
    if (!groups.has(currency)) groups.set(currency, []);
    groups.get(currency).push(row);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
}

function sourceLabel(metric) {
  const origin = metric.source || metric.collector || "extension";
  return `Source: ${origin === "extension" ? "Extension sync" : origin}`;
}

export default function PlatformFinance() {
  const { platforms, metrics, loading, error, reload } = usePlatformData();
  const [period, setPeriod] = useState("30d");
  const [platform, setPlatform] = useState("all");
  const [account, setAccount] = useState("all");
  const start = periodStart(period);
  const accounts = platforms.flatMap((item) => (item.accounts || []).map((entry) => ({
    ...entry,
    platformId: item.id,
    platformName: item.name,
  })));
  const visibleAccounts = account === "all"
    ? accounts
    : accounts.filter((entry) => `${entry.platformId}:${entry.account_id}` === account);
  const rows = useMemo(() => latestFinancialObservations(metrics).filter((metric) => {
    if (platform !== "all" && metric.platform !== platform) return false;
    if (account !== "all" && `${metric.platform}:${metric.account_id}` !== account) return false;
    if (start && new Date(metric.captured_at || 0) < start) return false;
    return true;
  }), [metrics, platform, account, start]);

  return <AppLayout title="Finance" overline="EXTENSION-SYNCED OBSERVATIONS">
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-col gap-3 border-b border-zinc-800 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Platform Finance</h1>
          <p className="mt-1 max-w-2xl text-sm leading-5 text-zinc-400">Read-only financial observations captured from connected platform accounts. Values are shown as reported; snapshots are not summed or estimated.</p>
        </div>
        <button type="button" onClick={reload} className="inline-flex min-h-10 items-center justify-center gap-2 rounded border border-zinc-700 px-3 text-sm text-zinc-200 hover:border-violet-500">
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <label className="min-w-0 text-xs text-zinc-500">Period
          <select aria-label="Finance period" value={period} onChange={(event) => setPeriod(event.target.value)} className="mt-1 min-h-10 w-full rounded border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200">
            {PERIODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="min-w-0 text-xs text-zinc-500">Platform
          <select aria-label="Finance platform" value={platform} onChange={(event) => { setPlatform(event.target.value); setAccount("all"); }} className="mt-1 min-h-10 w-full rounded border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200">
            <option value="all">All platforms</option>
            {platforms.filter((item) => item.state === "connected").map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label className="min-w-0 text-xs text-zinc-500">Account
          <select aria-label="Finance account" value={account} onChange={(event) => setAccount(event.target.value)} className="mt-1 min-h-10 w-full rounded border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200">
            <option value="all">All accounts</option>
            {visibleAccounts.filter((entry) => platform === "all" || entry.platformId === platform).map((entry) => <option key={`${entry.platformId}:${entry.account_id}`} value={`${entry.platformId}:${entry.account_id}`} label={`${entry.platformName} · ${entry.username || entry.account_id}`} />)}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="rounded border border-red-900 bg-red-950/30 px-4 py-3 text-sm text-red-300">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2 border-y border-zinc-800 py-3 text-xs text-zinc-500">
        <span>{rows.length} latest observations · {new Set(rows.map((row) => `${row.platform}:${row.account_id}`)).size} accounts</span>
        <span>Currency snapshots are not aggregated.</span>
      </div>

      {loading ? <p className="py-10 text-center text-sm text-zinc-500">Loading extension-synced finance data…</p> : !rows.length ? (
        <div className="rounded border border-zinc-800 bg-zinc-950/50 px-4 py-12 text-center">
          <p className="text-sm text-zinc-300">No financial observations are available for this selection.</p>
          <p className="mt-1 text-xs text-zinc-600">Connect an account and sync a page containing visible financial data.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupByCurrency(rows).map(([currency, group]) => <section key={currency} className="space-y-2" data-testid={`finance-currency-${currency}`}>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{currency} · {group.length} observation{group.length === 1 ? "" : "s"}</h2>
          {group.map((metric) => <article key={metric.metric_id || `${metric.platform}:${metric.account_id}:${metric.metric_key}`} className="grid min-w-0 gap-3 rounded border border-zinc-800 bg-zinc-950/70 p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
            <div className="min-w-0">
              <div className="break-words text-sm font-medium text-zinc-100">{metric.original_label || metric.metric_key}</div>
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-zinc-500">
                <span className="rounded border border-zinc-800 px-1.5 text-zinc-400" data-testid="finance-source-label">{sourceLabel(metric)}</span>
                <span>{metric.platform}</span><span>{metric.account_id}</span>
                {metric.period_start && <span>{metric.period_start}{metric.period_end ? ` – ${metric.period_end}` : ""}</span>}
                <span>{metric.captured_at ? new Date(metric.captured_at).toLocaleString() : "Capture time unavailable"}</span>
              </div>
            </div>
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 sm:justify-end">
              <div className="sm:text-right">
                <div className="break-all text-sm font-semibold text-white">{metricDisplayValue(metric)}</div>
                <div className={`mt-1 text-[11px] ${metric.parse_status === "unparsed" ? "text-amber-300" : "text-zinc-500"}`}>
                  {metric.parse_status === "unparsed" ? "Unparsed · raw value preserved" : metric.data_type || "Reported currency"}
                </div>
              </div>
              {metric.source_url && <a href={metric.source_url} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center gap-1 rounded border border-zinc-800 px-2.5 text-xs text-violet-300 hover:border-violet-500" aria-label={`Open source for ${metric.original_label || metric.metric_key}`}>
                Source <ExternalLink size={12} />
              </a>}
            </div>
          </article>)}
          </section>)}
        </div>
      )}
    </div>
  </AppLayout>;
}